import * as React from "react";
import { useCallback, useState } from "react";
import styled from "@mui/material/styles/styled";

import NoDiseaseIcon from "../../../assets/noDiseases.svg";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { useDaoContext } from "fe-web-components/diseaseview/DaoContext";
import { getCurrentUserEmail } from "user-management/src/cognito/kahunUserAuth";
import { useCreateSharedCase } from "../hooks/useCreateSharedCase";
import { useShareLinks } from "../../../saveCase/useShareLinks";
import { useDiagnosticAgentType } from "../../../context/diagnosticAgent";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { isProduction } from "fe-common/utils/envGetter";
import { getVersion } from "../../../VersionInfo";
import { DDStatusData } from "../updateDDToState";
import { getCurrentTime } from "fe-common/utils/time";

//region [[ Styles ]]

const DDErrorIconView = styled("div")(({ theme }) => ({
  marginTop: "25%",
  display: "flex",
  flexDirection: "column",
}));

const LinkText = styled("div")(({ theme }) => ({
  textDecoration: "underline",
  display: "inline",
  cursor: "Pointer",
  color: KahunColorMap.Primary,
}));

const DDErrorText = styled("div")(({ theme }) => ({
  marginTop: "20px",
  fontSize: "16px",
  textAlign: "center",
  color: KahunColorMap.DefaultLight,
}));

//endregion [[ Styles ]]

//region [[ Props ]]

export interface DDErrorIconProps {
  ddStatus: DDStatusData;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const DDErrorIcon = ({ ...props }: DDErrorIconProps) => {
  const dao = useDaoContext();

  const [agentType] = useDiagnosticAgentType();

  const createSharedCase = useCreateSharedCase(agentType);

  const { shareLink } = useShareLinks();

  const [feedbackSent, setFeedbackSent] = useState(false);

  const sendFeedback = useCallback(async () => {
    if (!isProduction) {
      return;
    }

    const feedbackId = props.ddStatus.uuid;
    const error = props.ddStatus.data;

    const errorData =
      error.message +
      "\n" +
      error.name +
      "\nmixpanelId=" +
      props.ddStatus.mixpanelId +
      "\nfeedbackId=" +
      feedbackId +
      "\ntime=" +
      getCurrentTime() +
      "\n" +
      error.stack +
      "\n";

    const text = `Auto-feedback - Empty diseases list due to technical issue - v${getVersion()}\n userAgent:${
      window?.navigator?.userAgent
    }\nfeedbackId:${feedbackId}\ntime=${getCurrentTime()}\nerror=${errorData}`;

    const userName = await getCurrentUserEmail();

    let shareCaseLink = "";

    const patientCaseId = await createSharedCase();

    if (patientCaseId) {
      shareCaseLink = shareLink(patientCaseId);
    }

    await dao.sendFeedback(text, shareCaseLink, userName);

    setFeedbackSent(true);

    evt({
      category: CATEGORY.DD_STATUS,
      action: ACTION.USER_SEND_ERROR_FEEDBACK,
      label: errorData,
    });
  }, [setFeedbackSent, createSharedCase, dao, shareLink, props.ddStatus]);

  return (
    <DDErrorIconView>
      <img src={NoDiseaseIcon} alt={"No Diseases"} height={"150px"} />
      {!feedbackSent && (
        <DDErrorText>
          {"Seems like we have a technical issue."} <br />
          {"Please click "}
          <LinkText onClick={sendFeedback}>here</LinkText>
          {" to share your case with our support team"}
        </DDErrorText>
      )}
      {feedbackSent && (
        <DDErrorText>
          {"Thanks, the case was shared with Kahun's support team"} <br />
        </DDErrorText>
      )}
    </DDErrorIconView>
  );
};
